import axiosClient from "./axios-client/axiosClient.jsx";

/* UsersAPI — thin client for BACKEND_TERMINAL_SPEC_V3 §J endpoints.
   Multi-user / maker-checker. All mutations send Idempotency-Key
   per V1 §E.1 convention. Returns raw axios responses so callers
   can read `data` and branch on status codes themselves. */

const idempotencyKey = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const auth = (token) => ({ headers: { Authorization: `Bearer ${token}` } });
const authIdem = (token, key) => ({
  headers: {
    Authorization: `Bearer ${token}`,
    "Idempotency-Key": key || idempotencyKey(),
    "Content-Type": "application/json",
  },
});

class UsersAPI {
  /* §J.3 — list users for the caller's client. */
  list(token) {
    return axiosClient.get("/users", auth(token));
  }

  /* §J.3 — get one. */
  get(token, userId) {
    return axiosClient.get(`/users/${encodeURIComponent(userId)}`, auth(token));
  }

  /* §J.3 — invite. BE sends the invite email. Idempotent. */
  invite(token, { email, name, role }, key) {
    return axiosClient.post("/users/invite", { email, name, role }, authIdem(token, key));
  }

  /* §J.3 — edit user (name, role, status). */
  update(token, userId, patch, key) {
    return axiosClient.patch(`/users/${encodeURIComponent(userId)}`, patch, authIdem(token, key));
  }

  /* §J.3 — soft delete (sets status=removed). */
  remove(token, userId, key) {
    return axiosClient.delete(`/users/${encodeURIComponent(userId)}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Idempotency-Key": key || idempotencyKey(),
      },
    });
  }

  /* §J.3 — transfer the OWNER role to another existing user. */
  transferOwnership(token, newOwnerUserId, key) {
    return axiosClient.post(
      "/users/transfer_ownership",
      { new_owner_user_id: newOwnerUserId },
      authIdem(token, key),
    );
  }

  /* §J.3 — no auth required; called from the invite-acceptance flow. */
  acceptInvite({ invite_token, password, name }, key) {
    return axiosClient.post(
      "/users/invite/accept",
      { invite_token, password, name },
      {
        headers: {
          "Idempotency-Key": key || idempotencyKey(),
          "Content-Type": "application/json",
        },
      },
    );
  }

  /* §J.3 — returns the permission matrix for the caller's role.
     Hooks/components use this to hide affordances deterministically. */
  getPermissions(token) {
    return axiosClient.post("/auth/get_permissions", {}, auth(token));
  }
}

export const usersAPI = new UsersAPI();
export const ROLES = ["OWNER", "ADMIN", "MAKER", "CHECKER", "MAKER_CHECKER", "VIEWER"];

/* User-facing labels for the role enum. Lives alongside the API
   client so any caller can render a role without re-translating. */
export const ROLE_LABELS = {
  OWNER:         "Owner",
  ADMIN:         "Admin",
  MAKER:         "Maker",
  CHECKER:       "Checker",
  MAKER_CHECKER: "Maker + Checker",
  VIEWER:        "Viewer",
};

export const ROLE_HELP = {
  OWNER:         "Full control plus user management. Exactly one per organization.",
  ADMIN:         "Full control except cannot remove the Owner.",
  MAKER:         "Can initiate payments and other actions; cannot approve.",
  CHECKER:       "Can approve maker submissions; cannot initiate.",
  MAKER_CHECKER: "Can initiate and approve, but never their own submissions.",
  VIEWER:        "Read-only access to the dashboard.",
};

export const USER_STATUSES = ["pending_invite", "active", "suspended", "removed"];
export const USER_STATUS_LABELS = {
  pending_invite: "Invite sent",
  active:         "Active",
  suspended:      "Suspended",
  removed:        "Removed",
};
